import type { StorageRecoverability, StorageResourceRef } from '../../../../types/azureStorage';
import { commonParent, deleteConfirmationCopy, type DeleteConfirmationItem, type StorageDeleteConfirmation } from './deleteConfirmationCopy';

/** One line per recovery fact (review U15); soft delete decides whether the delete can be undone. */
function blobRecovery(recoverability: StorageRecoverability): { lines: string[]; irreversible: boolean } {
  const state = recoverability.blobSoftDelete;
  const softDelete = state.state === 'on' ? `Soft delete: on — deleted blobs can be restored for ${state.days} days.`
    : state.state === 'off' ? 'Soft delete: off — deleted blobs cannot be restored.'
    : "Soft delete: unknown — don't count on restoring deleted blobs.";
  return {
    lines: [softDelete, 'Snapshots: deleted together with their blobs.', "Versioning: unknown — don't count on earlier versions being kept."],
    irreversible: state.state !== 'on',
  };
}

const folderName = (prefix: string): string => (prefix && !prefix.endsWith('/') ? `${prefix}/` : prefix);
const pathOf = (resource: StorageResourceRef): string => (resource.kind === 'blobPrefix' ? folderName(resource.prefix) : resource.kind === 'blob' ? resource.name : '');
const leaf = (path: string): string => path.replace(/\/+$/u, '').split('/').pop() ?? path;

/** What one selected item's delete removes (review E F8: a folder delete is recursive and includes history). */
function describeItem(resource: StorageResourceRef, parent: string): DeleteConfirmationItem {
  if (resource.kind === 'blobPrefix') return { label: `${leaf(folderName(resource.prefix))}/`, folder: true, detail: 'and everything under it, including snapshots and listed earlier versions' };
  if (resource.kind === 'container') return { label: `container ${resource.container}`, folder: true };
  if (resource.kind !== 'blob') return { label: resource.kind, folder: false };
  const label = parent && resource.name.startsWith(parent) ? resource.name.slice(parent.length) : resource.name;
  if (resource.snapshot) return { label, folder: false, detail: `only snapshot ${resource.snapshot}` };
  if (resource.versionId) return { label, folder: false, detail: `only version ${resource.versionId}` };
  return { label, folder: false };
}

function singleTitle(resource: StorageResourceRef): string {
  if (resource.kind === 'blobPrefix') return `Delete folder ${folderName(resource.prefix)}?`;
  return resource.kind === 'blob' ? `Delete ${resource.name}?` : 'Delete selected item?';
}

export function buildStorageDeleteConfirmation(
  resources: readonly StorageResourceRef[],
  recoverability: StorageRecoverability,
): StorageDeleteConfirmation {
  const container = resources.length === 1 && resources[0].kind === 'container' ? resources[0].container : undefined;
  if (container) {
    return {
      title: `Delete container ${container}?`,
      description: 'Container soft-delete state is unknown; treat this deletion as not recoverable.',
      confirmLabel: 'Delete container',
      requireTypedName: { value: container, label: `Type “${container}” to confirm` },
    };
  }
  const first = resources[0];
  const inOneContainer = first && 'container' in first && resources.every((resource) => 'container' in resource && resource.container === first.container);
  const parent = inOneContainer ? commonParent(resources.map(pathOf)) : '';
  const recovery = blobRecovery(recoverability);
  return deleteConfirmationCopy({
    items: resources, describe: (resource) => describeItem(resource, parent), singleTitle,
    location: inOneContainer && first && 'container' in first ? `${first.container}/${parent}` : undefined,
    recovery: recovery.lines, irreversible: recovery.irreversible,
  });
}
